import { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { GLOBAL_IMAGES, YearBg } from "../../assets/asset";
import CallAllTypeFromHere from "../CallAllTypeFromHere/CallAllTypeFromHere";
import { SkeletonVideo } from "../../utils/SkeletonLoader";
import CustomButton from "../../assets/buttons/CustomButton";
import { useDispatch, useSelector } from "react-redux";
import { setActiveYear } from "../../store/slice/scrollPositionSlice";
import { setMuteValue } from "../../store/slice/muteSlice";
import Lottie from "lottie-react";
import loadingAnimation from "../../assets/global/loading.json";
import { Loader2 } from "lucide-react";

const FoundationLessonCom = () => {
  const { token } = useSelector((state) => state.user);
  const { olderValueWas } = useSelector((state) => state.mute);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [lessonData, setLessonData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showTraceCom, setShowTraceCom] = useState(false);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const [videoShared, setVideoShared] = useState("");

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  let storedLessonContext = {};
  try {
    storedLessonContext = JSON.parse(
      sessionStorage.getItem("lessonContext") || "{}"
    );
  } catch (_) {
    storedLessonContext = {};
  }

  const lessonId = Number(
    location.state?.lessonId ??
      searchParams.get("lessonId") ??
      storedLessonContext.lessonId
  );
  const yearId = Number(
    location.state?.yearId ??
      searchParams.get("yearId") ??
      storedLessonContext.yearId
  );
  const colorTheme =
    location.state?.colorTheme ?? storedLessonContext.colorTheme ?? "#000000";
  const lessonDisplayName =
    location.state?.lessonDisplayName ??
    searchParams.get("lessonDisplayName") ??
    storedLessonContext.lessonDisplayName ??
    "";
  const isLessonContextValid =
    Number.isFinite(lessonId) &&
    lessonId > 0 &&
    Number.isFinite(yearId) &&
    yearId > 0;

  const fetchData = useCallback(async () => {
    if (!token || !isLessonContextValid) {
      setLoading(false);
      return;
    }
    setLoading(true);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}api/questionoflession`,
        {
          method: "POST",
          headers: {
            Authorization: `${process.env.REACT_APP_AUTH_TOKEN_DEV}`,
            "Content-Type": "application/json",
            jwt: token,
          },
          body: JSON.stringify({ LessionId: lessonId }),
        }
      );

      if (!response.ok)
        throw new Error(`HTTP error! Status: ${response.status}`);

      const result = await response.json();
      if (
        result?.status === "Fail" &&
        (result?.status_code === 304 ||
          result?.message === "jwt token is not valid")
      ) {
        navigate("/login", { replace: true });
        return;
      }

      setLessonData(result);

      const videoUrl = result?.videourl?.[0]?.fields?.videourl || "";
      setVideoShared(videoUrl);

      if (result?.isTestLesson || !videoUrl) {
        setShowTraceCom(true);
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, [token, lessonId, isLessonContextValid, navigate]);

  useEffect(() => {
    if (!isLessonContextValid) {
      navigate("/lessonDashboard", { replace: true });
      return;
    }
    if (token) fetchData();
  }, [token, fetchData, isLessonContextValid, navigate]);

  useEffect(() => {
    if (!Number.isFinite(yearId) || yearId <= 0) return;
    dispatch(setActiveYear(yearId));
    dispatch(setMuteValue(true));

    return () => {
      dispatch(setMuteValue(olderValueWas));
    };
  }, [yearId]);

  const handleVideoEnd = () => {
    setShowTraceCom(true);
  };

  const handleVideoLoad = () => {
    setIsVideoLoaded(true);
  };

  if (loading)
    return (
      <div className="flex items-center justify-center h-screen w-screen bg-white">
        {/* <Lottie className="w-48 h-48" pbadges /> */}
        <Loader2 className="h-12 w-12 animate-spin text-gray-600" />
      </div>
    );

  if (error) return <div>Error: {error}</div>;

  console.log({ isVideoLoaded });

  return (
    <div
      className={`relative min-h-screen flex flex-col items-center bg-blue-50 font-mali overflow-y-auto ${
        isVideoLoaded ? "" : "bg-transparent"
      }`}
      style={{
        backgroundImage: `url(${YearBg[yearId - 1] || GLOBAL_IMAGES.onboarding_bg})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        width: "100vw",
        minHeight: "100vh",
      }}
    >
      {/* Background dim layer only while video is active */}
      {!showTraceCom && (
        <div className="absolute inset-0 bg-black/40 pointer-events-none"></div>
      )}

      {/* Video Phase */}
      {!showTraceCom &&
        lessonData &&
        !lessonData.isTestLesson &&
        videoShared && (
          <div className="relative  top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10 flex flex-col items-center">
            {!isVideoLoaded && <SkeletonVideo />}
            <video
              className={`w-[69%] h-auto bg-gray-900 max-w-[82%] ${
                !isVideoLoaded ? "hidden" : ""
              }`}
              src={videoShared}
              controls
              autoPlay
              onEnded={handleVideoEnd}
              onLoadedData={handleVideoLoad}
            />

            <div className="h-20 w-[69%] mt-4 flex justify-end items-center">
              <CustomButton
                text={"Continue"}
                onClick={handleVideoEnd}
                disabled={false}
                isShaking={false}
                isIcon={true}
                type={"correct"}
              />
            </div>
          </div>
        )}

      {/* CallAllType Phase */}
      {showTraceCom && lessonData && (
        <div className="relative z-10 w-full">
          <CallAllTypeFromHere
            lessonData={lessonData.data}
            yearId={yearId}
            video={videoShared}
            colorTheme={colorTheme}
            lessonDisplayName={lessonDisplayName}
          />
        </div>
      )}
    </div>
  );
};

export default FoundationLessonCom;
